import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AddSubjects.css';

const API = 'https://mykaddle-api.onrender.com/api';
const MAX_SUBJECTS = 3;

const AddSubjects = () => {
  const navigate = useNavigate();

  const [allSubjects, setAllSubjects] = useState([]);
  const [offeredSubjects, setOfferedSubjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSubject, setAddSubject] = useState(null);
  const [addDesc, setAddDesc] = useState('');
  const [addRate, setAddRate] = useState('');
  const [addCycle, setAddCycle] = useState('monthly');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubject, setEditSubject] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editDescCount, setEditDescCount] = useState(0);

  // Remove modal state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeSubject, setRemoveSubject] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── Fetch data ──
  const fetchAll = useCallback(async () => {
    try {
      const [subjectsRes, profileRes] = await Promise.all([
        axios.get(`${API}/subjects`),
        axios.get(`${API}/tutor/profile`, { headers: authHeaders })
      ]);
      setAllSubjects(subjectsRes.data);
      setOfferedSubjects(profileRes.data.offeredSubjects || []);
    } catch (err) {
      console.error('Failed to load data', err);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Search ──
  const handleSearch = (val) => {
    setSearchQuery(val);
    if (val.trim()) {
      const q = val.toLowerCase();
      setFilteredSubjects(allSubjects.filter(s =>
        s.displayName.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q)
      ));
      setShowDropdown(true);
    } else {
      setFilteredSubjects([]);
      setShowDropdown(false);
    }
  };

  // ── Add subject ──
  const openAddModal = (subject) => {
    if (offeredSubjects.length >= MAX_SUBJECTS) {
      alert('You can only offer up to 3 subjects');
      return;
    }
    setAddSubject(subject);
    setAddDesc('');
    setAddRate('');
    setAddCycle('monthly');
    setShowAddModal(true);
    setShowDropdown(false);
  };

  const handleAddSubject = async () => {
    if (!addDesc.trim() || !addRate) {
      alert('Please fill in all fields');
      return;
    }
    try {
      const subjectId = 'GRP-' + Math.random().toString(36).substring(2, 8).toUpperCase();
      await axios.post(`${API}/tutor/add-subject`, {
        subjectId,
        subject: addSubject._id,
        subjectDisplayName: addSubject.displayName,
        subjectCategory: addSubject.category,
        subjectGradeLevels: addSubject.gradeLevels || [],
        description: addDesc,
        rate: parseFloat(addRate),
        billingCycle: addCycle,
        currency: 'Rands'
      }, { headers: authHeaders });

      setShowAddModal(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add subject');
    }
  };

  // ── Edit subject ──
  const openEditModal = (subject) => {
    setEditSubject(subject);
    setEditDesc(subject.description || '');
    setEditRate(subject.rate?.toString() || '');
    setEditDescCount(subject.description?.length || 0);
    setShowEditModal(true);
  };

  const handleEditSubject = async () => {
    if (!editDesc.trim() || !editRate) {
      alert('Please fill in all fields');
      return;
    }
    try {
      await axios.put(`${API}/tutor/subject/update`, {
        subjectId: editSubject.subjectId,
        description: editDesc,
        rate: parseFloat(editRate)
      }, { headers: authHeaders });

      setShowEditModal(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update subject');
    }
  };

  // ── Remove subject ──
  const openRemoveModal = (subject) => {
    setRemoveSubject(subject);
    setShowRemoveModal(true);
  };

  const handleRemoveSubject = async () => {
    try {
      await axios.put(`${API}/tutor/subject/toggle-status`, {
        subjectId: removeSubject.subjectId
      }, { headers: authHeaders });

      setShowRemoveModal(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove subject');
    }
  };

  const getCardColor = (index) => index % 2 === 0 ? 'card-maths' : 'card-science';

  return (
    <div className="ms-dashboard-container">
      <div className="ms-almighty-wrapper">

        {/* Top bar */}
        <div className="ms-top-bar">
          <div className="ms-top-left-header">
            <div className="ms-header-row">
              <button className="ms-back-icon-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none"/>
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
                </svg>
              </button>
              <span className="ms-manage-subjects-title">Manage subjects</span>
            </div>
            <p className="ms-sub-header">Add or request removal of your group subjects here. You can also adjust your rate and edit subject descriptions for tutees to view.</p>
          </div>

          {/* Search bar */}
          <div className="ms-search-bar-wrap">
            <div className="ms-search-bar-container">
              <span className="ms-search-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none"/>
                  <path fill="currentColor" d="M10.5 2a8.5 8.5 0 1 0 5.262 15.176l3.652 3.652a1 1 0 0 0 1.414-1.414l-3.652-3.652A8.5 8.5 0 0 0 10.5 2M4 10.5a6.5 6.5 0 1 1 13 0a6.5 6.5 0 0 1-13 0"/>
                </svg>
              </span>
              <input
                type="text"
                className="ms-search-input"
                placeholder="Search subjects…"
                autoComplete="off"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>

            {/* Glass dropdown */}
            <div className={`ms-search-dropdown ${showDropdown ? 'open' : ''}`}>
              <div className="ms-sdrop-header">
                {filteredSubjects.length > 0 ? `${filteredSubjects.length} result${filteredSubjects.length !== 1 ? 's' : ''}` : 'No results'}
              </div>
              <div className="ms-search-dropdown-body">
                {filteredSubjects.length === 0 ? (
                  <div className="ms-sdrop-notfound">
                    <div className="ms-sdrop-notfound-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                        <path d="M0 0h24v24H0z" fill="none"/>
                        <path fill="currentColor" d="M11.5 4A8.5 8.5 0 0 0 3 12.5a8.5 8.5 0 0 0 8.5 8.5a8.5 8.5 0 0 0 8.5-8.5A8.5 8.5 0 0 0 11.5 4m0-1a9.5 9.5 0 0 1 9.5 9.5a9.5 9.5 0 0 1-9.5 9.5A9.5 9.5 0 0 1 2 12.5A9.5 9.5 0 0 1 11.5 3M11 17h1v2h-1zm.5-11A3.5 3.5 0 0 1 15 9.5c0 .9-.7 1.5-1.44 2.18l-.93.9c-.59.67-.66 1.95-.63 2.39V15h-1c0-.05-.1-1.96.87-3.08l1.03-.99c.6-.53 1.1-.98 1.1-1.43A2.5 2.5 0 0 0 11.5 7A2.5 2.5 0 0 0 9 9.5H8A3.5 3.5 0 0 1 11.5 6"/>
                      </svg>
                    </div>
                    <div className="ms-sdrop-notfound-title">Not Found</div>
                    <div className="ms-sdrop-notfound-sub">Go to support and request that we add it</div>
                  </div>
                ) : (
                  filteredSubjects.map(s => (
                    <div key={s._id} className="ms-sdrop-item" onClick={() => openAddModal(s)}>
                      <div className="ms-sdrop-item-left">
                        <span className="ms-sdrop-item-name">{s.displayName}</span>
                        <span className="ms-sdrop-item-cat">{s.category}</span>
                      </div>
                      <button className="ms-sdrop-add-btn" onClick={(e) => { e.stopPropagation(); openAddModal(s); }}>
                        + Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main body */}
        <div className="ms-main-body">
          <div className="ms-cards-col">
            <div className="ms-cards-top-row">
              {offeredSubjects.map((subject, i) => (
                <div
                  key={subject.subjectId || i}
                  className={`ms-session-card ${getCardColor(i)}`}
                  onDoubleClick={() => openEditModal(subject)}
                >
                  <div className="ms-card-nm">{subject.subjectDisplayName}</div>
                  <div className="ms-card-descript">{subject.description || 'No description yet. Double-tap to edit.'}</div>
                  <div className="ms-card-bottom-row">
                    <div className="ms-card-cash">
                      <span>R</span>&nbsp;{subject.rate}
                      <span style={{ fontWeight: 400, fontSize: '0.72rem' }}>/{subject.billingCycle === 'weekly' ? 'week' : 'month'}</span>
                    </div>
                    <div className="ms-sub-delete" onClick={(e) => { e.stopPropagation(); openRemoveModal(subject); }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M0 0h48v48H0z" fill="none"/><path fill="currentColor" d="M20 10.5v.5h8v-.5a4 4 0 0 0-8 0m-2.5.5v-.5a6.5 6.5 0 1 1 13 0v.5h11.25a1.25 1.25 0 1 1 0 2.5h-2.917l-2 23.856A7.25 7.25 0 0 1 29.608 44H18.392a7.25 7.25 0 0 1-7.224-6.644l-2-23.856H6.25a1.25 1.25 0 1 1 0-2.5zm-3.841 26.147a4.75 4.75 0 0 0 4.733 4.353h11.216a4.75 4.75 0 0 0 4.734-4.353L36.324 13.5H11.676zM21.5 20.25a1.25 1.25 0 1 0-2.5 0v14.5a1.25 1.25 0 1 0 2.5 0zM27.75 19c.69 0 1.25.56 1.25 1.25v14.5a1.25 1.25 0 1 1-2.5 0v-14.5c0-.69.56-1.25 1.25-1.25"/></svg>
                      <span>Request Removal</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Empty cards to fill up to 3 */}
              {Array.from({ length: MAX_SUBJECTS - offeredSubjects.length }).map((_, i) => (
                <div key={`empty-${i}`} className="ms-session-card-empty">
                  <div className="ms-empty-plus-ring">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                  <div className="ms-empty-card-label">Add a subject</div>
                  <div className="ms-empty-card-sub">No subject chosen yet. Search above to add one to your profile.</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Info bar */}
        <div className="ms-info-bar">
          <div className="ms-info-bar-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path fill="#f3f800" d="M12 2.5c-3.81 0-6.5 2.743-6.5 6.119c0 1.536.632 2.572 1.425 3.56c.172.215.347.422.527.635l.096.112c.21.25.427.508.63.774c.404.531.783 1.128.995 1.834a.75.75 0 0 1-1.436.432c-.138-.46-.397-.89-.753-1.357a18 18 0 0 0-.582-.714l-.092-.11c-.18-.212-.37-.436-.555-.667C4.87 12.016 4 10.651 4 8.618C4 4.363 7.415 1 12 1s8 3.362 8 7.619c0 2.032-.87 3.397-1.755 4.5c-.185.23-.375.454-.555.667l-.092.109c-.21.248-.405.481-.582.714c-.356.467-.615.898-.753 1.357a.751.751 0 0 1-1.437-.432c.213-.706.592-1.303.997-1.834c.202-.266.419-.524.63-.774l.095-.112c.18-.213.355-.42.527-.634c.793-.99 1.425-2.025 1.425-3.561C18.5 5.243 15.81 2.5 12 2.5M8.75 18h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5m.75 3.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75"/>
            </svg>
          </div>
          <p className="ms-info-bar-text">
            To update your current rate or subject description <strong>double tap on the item to edit</strong> and confirm to make changes.
            Please note that <strong>billing cycles can never be changed</strong> — rather opt to delete the subject entirely.
          </p>
        </div>
      </div>

      {/* ═══ ADD SUBJECT MODAL ═══ */}
      {showAddModal && (
        <div className="ms-modal-backdrop open" onClick={() => setShowAddModal(false)}>
          <div className="ms-modal" onClick={e => e.stopPropagation()}>
            <button className="ms-modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            <div className="ms-modal-icon-ring ms-green">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            </div>
            <div className="ms-modal-title">Add subject</div>
            <div className="ms-modal-sub">Set up your teaching details for this subject. Students will see this on your profile.</div>

            <div className="ms-subject-preview-chip">
              <div className="ms-subject-preview-info">
                <span className="ms-subject-preview-name">{addSubject?.displayName || '—'}</span>
                <span className="ms-subject-preview-cat">{addSubject?.category || '—'}</span>
              </div>
            </div>

            <div className="ms-modal-field">
              <div className="ms-modal-label-row">
                <label className="ms-modal-label">Teaching description</label>
                <span className={`ms-modal-char-count ${addDesc.length >= 270 ? 'warn' : ''}`}>{addDesc.length} / 300</span>
              </div>
              <textarea className="ms-modal-textarea" maxLength="300" placeholder="Describe your teaching approach, topics covered, and session format…" value={addDesc} onChange={e => setAddDesc(e.target.value)} />
            </div>

            <div className="ms-modal-field">
              <label className="ms-modal-label">Billing cycle</label>
              <div className="ms-cycle-toggle">
                <button className={`ms-cycle-btn ${addCycle === 'monthly' ? 'active' : ''}`} onClick={() => setAddCycle('monthly')}>Monthly</button>
                <button className={`ms-cycle-btn ${addCycle === 'weekly' ? 'active' : ''}`} onClick={() => setAddCycle('weekly')}>Weekly</button>
              </div>
            </div>

            <div className="ms-modal-field">
              <label className="ms-modal-label">Rate per {addCycle} (R)</label>
              <div className="ms-rate-input-wrap">
                <span className="ms-rate-prefix">R</span>
                <input type="number" className="ms-modal-input" placeholder="e.g. 280" min="0" value={addRate} onChange={e => setAddRate(e.target.value)} />
              </div>
            </div>

            <div className="ms-modal-actions">
              <button className="ms-btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="ms-btn-confirm-green" onClick={handleAddSubject}>Add subject</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ EDIT SUBJECT MODAL ═══ */}
      {showEditModal && (
        <div className="ms-modal-backdrop open" onClick={() => setShowEditModal(false)}>
          <div className="ms-modal" onClick={e => e.stopPropagation()}>
            <button className="ms-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            <div className="ms-modal-icon-ring ms-green">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </div>
            <div className="ms-modal-title">Edit — {editSubject?.subjectDisplayName}</div>
            <div className="ms-modal-sub">Changes to your description and rate will be visible to students. Billing cycle cannot be modified.</div>

            <div className="ms-modal-field">
              <label className="ms-modal-label">Monthly rate (R)</label>
              <div className="ms-rate-input-wrap">
                <span className="ms-rate-prefix">R</span>
                <input type="number" className="ms-modal-input" placeholder="e.g. 280" min="0" value={editRate} onChange={e => setEditRate(e.target.value)} />
              </div>
            </div>

            <div className="ms-modal-field">
              <div className="ms-modal-label-row">
                <label className="ms-modal-label">Subject description</label>
                <span className={`ms-modal-char-count ${editDescCount >= 270 ? 'warn' : ''}`}>{editDescCount} / 300</span>
              </div>
              <textarea className="ms-modal-textarea" maxLength="300" placeholder="Describe your sessions…" value={editDesc} onChange={e => { setEditDesc(e.target.value); setEditDescCount(e.target.value.length); }} />
            </div>

            <div className="ms-modal-actions">
              <button className="ms-btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="ms-btn-confirm-green" onClick={handleEditSubject}>Confirm changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ REMOVE MODAL ═══ */}
      {showRemoveModal && (
        <div className="ms-modal-backdrop open" onClick={() => setShowRemoveModal(false)}>
          <div className="ms-modal" onClick={e => e.stopPropagation()}>
            <button className="ms-modal-close" onClick={() => setShowRemoveModal(false)}>✕</button>
            <div className="ms-modal-icon-ring ms-red">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M0 0h48v48H0z" fill="none"/><path fill="currentColor" d="M20 10.5v.5h8v-.5a4 4 0 0 0-8 0m-2.5.5v-.5a6.5 6.5 0 1 1 13 0v.5h11.25a1.25 1.25 0 1 1 0 2.5h-2.917l-2 23.856A7.25 7.25 0 0 1 29.608 44H18.392a7.25 7.25 0 0 1-7.224-6.644l-2-23.856H6.25a1.25 1.25 0 1 1 0-2.5zm-3.841 26.147a4.75 4.75 0 0 0 4.733 4.353h11.216a4.75 4.75 0 0 0 4.734-4.353L36.324 13.5H11.676zM21.5 20.25a1.25 1.25 0 1 0-2.5 0v14.5a1.25 1.25 0 1 0 2.5 0zM27.75 19c.69 0 1.25.56 1.25 1.25v14.5a1.25 1.25 0 1 1-2.5 0v-14.5c0-.69.56-1.25 1.25-1.25"/></svg>
            </div>
            <div className="ms-modal-title">Request subject removal</div>
            <div className="ms-modal-sub">You are requesting removal of <strong>{removeSubject?.subjectDisplayName}</strong> from your profile.</div>
            <div className="ms-removal-warning">
              This action will remove the subject and all associated sessions from your profile. Students currently enrolled will be notified. This cannot be undone — if you need this subject back, you will need to re-add it.
            </div>
            <div className="ms-modal-actions">
              <button className="ms-btn-cancel" onClick={() => setShowRemoveModal(false)}>Cancel</button>
              <button className="ms-btn-confirm-red" onClick={handleRemoveSubject}>Yes, request removal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSubjects;